import GppGoodIcon from '@mui/icons-material/GppGood'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo } from 'react'
import { getEffectiveSpaceTypes } from 'src/components/SearchBar/SpaceTypeFilters'
import { SpaceType } from 'src/components/SearchBar/types'
import EmptySearchSummary from 'src/components/SearchManager/EmptySearchSummary'
import { PillarPage } from 'src/constants/searchPages'
import { useABTest } from 'src/hooks/useABTest'
import useBreakpoints from 'src/hooks/useBreakpoints'
import {
  CARD_LAYOUT_EXPERIMENT_ID,
  currentCardLayout
} from 'src/utils/cardLayoutExperiment'
import { directoryGuideMode, guidePillarFor } from 'src/utils/directoryGuide'
import { toLocationDisplayName } from 'src/utils/locationLabel'
import {
  resolveOnDemandCity,
  resolveOnDemandCityByCoords
} from 'src/utils/onDemand'
import { NEAR_USER } from '../../constants'
import { useFilterProvider } from '../../context/FilterProvider'
import { ILocation } from '../../types/graphql'
import { IQueryParams } from '../../types/types'
import BuenFinBanner from '../Banners/BuenFinBanner'
import DirectoryFloorMount from '../DirectoryFacility/DirectoryFloorMount'
import DirectoryZeroSupply from '../DirectoryFacility/DirectoryZeroSupply'
import LoadingCards from '../Loaders/SearchLoader'
import LocationSearchCard from '../LocationSearchCard/SearchCard'
import { shouldShowLogisticsCard } from './shouldShowLogisticsCard'

const CaptureLead = dynamic(() => import('./CaptureLead'))
const HostCTA = dynamic(() => import('./HostCTA'))
const MarketNotAvailable = dynamic(() => import('./EmptyState'))
const OnDemandSearchCard = dynamic(() => import('./OnDemandSearchCard'))
const LogisticsSearchCard = dynamic(() => import('./LogisticsSearchCard'))

/** Position (0-indexed) where the on-demand cross-link card is inserted into
 * the listings grid when coverage is active. Index 2 keeps the card above the
 * fold on mobile without dominating; listings before/after stay in their
 * relative order. */
const ON_DEMAND_CARD_INDEX = 2

/** Position (0-indexed) where the Soluciones Logísticas (3PL) cross-link card
 * is inserted when `shouldShowLogisticsCard` passes. Deliberately a
 * DIFFERENT index than `ON_DEMAND_CARD_INDEX`: the two gates are disjoint by
 * URL pillar (SelfStorage vs Warehouse/Industrial can never both match a
 * `[pillar]/[city]` route), but on the unified `/search` route a multi-select
 * space-type filter CAN legally include both `SELF_STORAGE` and `WAREHOUSE`
 * at once — in that edge case both cards render, at different grid
 * positions, instead of double-pushing at the same slot. */
const LOGISTICS_CARD_INDEX = 4

const MAX_LOC = 50
const MIN_LOCS_FORM = 8

/** SpaceType / PillarPage → `host_cta:SPACE_TYPE.*` slug. Used by the count
 * pill in the results-meta strip to render specific plurals ("10 Mini
 * Bodegas") instead of the generic "10 spots". Land + ALL intentionally
 * absent — host_cta has no plural for Land, and ALL stays generic. */
const SPACE_TYPE_TO_HOST_CTA_SLUG: Partial<Record<SpaceType, string>> = {
  [SpaceType.SELF_STORAGE]: 'self-storage',
  [SpaceType.PARKING]: 'parking',
  [SpaceType.WAREHOUSE]: 'warehouse',
  [SpaceType.INDUSTRIAL_WAREHOUSE]: 'industrial-warehouse'
}

const PILLAR_TO_HOST_CTA_SLUG: Record<PillarPage, string> = {
  [PillarPage.SelfStorage]: 'self-storage',
  [PillarPage.Parking]: 'parking',
  [PillarPage.Warehouse]: 'warehouse',
  [PillarPage.Industrial]: 'industrial-warehouse'
}

/** Reads the current city slug from the dynamic route segment
 * (`/[pillar]/[city]` and `/[pillar]/[city]/[hood]`) with a fallback to the
 * URL filter (deep-linked /search). Returns `undefined` when no city context
 * exists. */
export function getCurrentCitySlug(
  routerCity: string | string[] | undefined,
  filterCity: string | null | undefined
): string | undefined {
  const fromRoute = Array.isArray(routerCity) ? routerCity[0] : routerCity
  return fromRoute ?? filterCity ?? undefined
}

/** Pure gating logic for the on-demand search card. Exposed for tests; the
 * component reads pillar context (URL slug OR FilterProvider spaceTypes) +
 * the resolved city + lat/lng and forwards them here. Returns the resolved
 * on-demand market when the card should render, or `null` to suppress.
 *
 * Card renders only when:
 *  - context is SelfStorage — either the URL pillar (`/renta-mini-bodegas/...`)
 *    OR the FilterProvider's `spaceTypes` includes `SELF_STORAGE` / `ALL`
 *    (the unified `/search` route doesn't have a URL pillar; we fall back to
 *    the active space-type filter so the card still surfaces there)
 *  - the search point resolves to a covered on-demand market — PRIMARY by
 *    coords (`resolveOnDemandCityByCoords`) which covers every point inside
 *    the metro's `serviceArea` radius; FALLBACK by slug
 *    (`resolveOnDemandCity`) for searches that arrive without lat/lng (city
 *    pillar pages without geo, legacy callers, server-side renders)
 *  - that market has `partnerActive: true`
 *
 * Coords-first means hoods like "Cumbres" or "Carretera Nacional" — which we
 * never enumerated in `metroAreaCities` — also surface the card, and any
 * future market lights up automatically by adding `serviceArea` without
 * curating its hoods.
 */
export function shouldShowOnDemandSearchCard(
  routerPillar: string | string[] | undefined,
  citySlug: string | undefined,
  filterSpaceTypes?: SpaceType[],
  lat?: number,
  lng?: number
): ReturnType<typeof resolveOnDemandCity> {
  const pillar = Array.isArray(routerPillar) ? routerPillar[0] : routerPillar
  const isSelfStorageByUrl = pillar === PillarPage.SelfStorage
  const isSelfStorageByFilter =
    !pillar &&
    Array.isArray(filterSpaceTypes) &&
    (filterSpaceTypes.length === 0 ||
      filterSpaceTypes.includes(SpaceType.SELF_STORAGE) ||
      filterSpaceTypes.includes(SpaceType.ALL))
  if (!isSelfStorageByUrl && !isSelfStorageByFilter) return null

  // Coords, when we have them, are the FINAL word — not merely the preferred
  // one. They describe the point the renter is actually looking at, so a point
  // that lands in no service area means "not covered", never "unknown", and
  // must not fall through to the slug.
  //
  // The slug outlives the search that produced it: `updateParams` merges
  // `{...filters, ...initialFilters, ...params}`, so a `?city=` set by a
  // pillar page survives into the next `/search` URL unless the caller clears
  // it. Treating a definitive coords miss as "unknown" is what let a Cancún
  // search render "Almacena en Monterrey sin mover una caja" over 0 results.
  //
  // Slug matching stays the fallback for searches that genuinely arrive
  // without coords (legacy callers, server-side renders).
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng)
  const resolved = hasCoords
    ? resolveOnDemandCityByCoords(lat, lng)
    : resolveOnDemandCity(citySlug)
  if (!resolved || !resolved.partnerActive) return null

  return resolved
}

type ISearchListProps = {
  header?: React.JSX.Element
  seoFooter?: React.JSX.Element
  loading: boolean
  totalLocations: number | undefined
  locations: ILocation[] | undefined
  updateParams: (args: Partial<IQueryParams>) => void
  setSelectedLocation: (location?: ILocation | undefined) => void
  nearbySearchData?: import('./hostCtaQuery').NearbySearchCountsResult
  locationName?: string
  /** SSR-fetched directory rows for a ZERO-SUPPLY city page (2026-08-28). */
  initialDirectory?: ILocation[]
  priorityImages?: boolean
  /** Current 1-indexed results page, and how many there are. Used ONLY to hold
   *  the directory floor back until the renter has seen every SpotMe listing —
   *  see the mount below. */
  page?: number
  pageCount?: number
  /** Rows the directory floor will render — used only to reserve its height
   *  before it mounts, so its arrival shifts nothing. */
  directoryExpectedRows?: number
  /** Desktop hover link between a directory card and its map dot. */
  onDirectoryHoverChange?: (locationId: string | null) => void
  /** Coordinates the current search actually resolved to, from
   *  `googlePlaceDetails`. On `/search` the renter picks a place from the
   *  autocomplete and the URL carries `placeId` with NO lat/lng — the coords
   *  only exist once the backend resolves the place. Without this, the
   *  coords-first gate in `shouldShowOnDemandSearchCard` has nothing to work
   *  with on the one route where the stale-slug fallback is most dangerous.
   *  `filters.lat/lng` remain the source on pillar/city pages, which bake
   *  coords into `initialFilters` server-side. */
  searchCenter?: { lat?: number | null; lng?: number | null }
}

function SearchList({
  header,
  loading,
  totalLocations = 0,
  locations,
  page,
  pageCount,
  directoryExpectedRows,
  onDirectoryHoverChange,
  setSelectedLocation,
  nearbySearchData,
  locationName,
  initialDirectory,
  priorityImages,
  searchCenter
}: ISearchListProps) {
  const { t } = useTranslation('common')
  const { t: tHostCta } = useTranslation('host_cta')
  const { filters, isReady, isSliderOpen } = useFilterProvider()
  const { isDownMd } = useBreakpoints()
  const router = useRouter()
  // Exposure for the mobile card-layout experiment — phones below 650px only
  // (wider screens render the same card in both arms) and only once real
  // cards are listed. The arm itself is applied before paint by _document's
  // boot script. Effect-only input: it never changes the markup.
  useABTest(CARD_LAYOUT_EXPERIMENT_ID, {
    shouldTrack:
      isDownMd &&
      currentCardLayout() !== undefined &&
      (locations?.length ?? 0) > 0
  })

  const numberOfLocations = loading
    ? '...'
    : totalLocations > MAX_LOC
      ? `${MAX_LOC}+`
      : totalLocations

  // Neighbor pattern: "10 Mini Bodegas cerca de Del Valle". When the search
  // has no explicit location (raw /search or map-only zoom), drop the
  // location name in favour of the user-addressing "cerca de ti" — avoids
  // the awkward "cerca de Cerca de mí" double-up.
  // Reverse-map the raw description (a slug on bare /search, e.g.
  // ?description=san-pedro-garza-garcia) back to its accented display name so
  // the heading reads "cerca de San Pedro Garza García", not the literal slug.
  const locationDisplayName = toLocationDisplayName(filters.description)
  const locationLabel = locationDisplayName
    ? t('SEARCH_MANAGER.NEAR_LOCATION', { location: locationDisplayName })
    : t('SEARCH_MANAGER.NEAR_YOU')

  // Count-pill noun. Promotes the generic "spots" to a specific plural
  // ("Mini Bodegas", "Estacionamientos", "Bodegas Comerciales", "Naves
  // Industriales") when the search context is unambiguous. Source priority:
  //   1. `filters.spaceTypes` when the user picked exactly ONE specific type
  //      (multi-select and ALL stay generic — no defensible pluralization).
  //   2. URL pillar from `[pillar]/[city]/[hood]` — city/hood pages always
  //      carry a single pillar, so the strip on those pages reads
  //      "10 Mini Bodegas · Del Valle" instead of "10 spots · Del Valle".
  //   3. Fallback to "spots" (the original copy) for unscoped /search and
  //      Land — host_cta has no plural for Land.
  // Plural labels live in the `host_cta` namespace which is already loaded
  // on both /search and the city/hood pages.
  const countNoun = useMemo(() => {
    const types = filters.spaceTypes
    if (types?.length === 1 && types[0] !== SpaceType.ALL) {
      const slug = SPACE_TYPE_TO_HOST_CTA_SLUG[types[0]]
      if (slug) return tHostCta(`SPACE_TYPE.${slug}` as never)
    }
    const pillarRaw = router.query.pillar
    const pillar = Array.isArray(pillarRaw) ? pillarRaw[0] : pillarRaw
    const slug = pillar ? PILLAR_TO_HOST_CTA_SLUG[pillar as PillarPage] : null
    if (slug) return tHostCta(`SPACE_TYPE.${slug}` as never)
    return 'spots'
  }, [filters.spaceTypes, router.query.pillar, tHostCta])

  // On-demand cross-link card gating. Only the SelfStorage pillar surfaces the
  // card; other pillars keep their existing grid. Resolves the current city
  // (URL slug → on-demand market), falls back to the FilterProvider city for
  // deep-linked /search. The card never renders when results are empty —
  // Unit 12's `OnDemandRecoveryCard` owns that surface.
  // Prefer the resolved place coords over the URL's: on /search the URL has a
  // `placeId` and no lat/lng, so `filters.lat/lng` are undefined for the whole
  // search and the gate would otherwise be deciding on the slug alone.
  const gateLat = searchCenter?.lat ?? filters.lat
  const gateLng = searchCenter?.lng ?? filters.lng
  const onDemandResolution = useMemo(
    () =>
      shouldShowOnDemandSearchCard(
        router.query.pillar,
        getCurrentCitySlug(router.query.city, filters.city),
        filters.spaceTypes,
        gateLat ?? undefined,
        gateLng ?? undefined
      ),
    [
      router.query.pillar,
      router.query.city,
      filters.city,
      filters.spaceTypes,
      gateLat,
      gateLng
    ]
  )

  // Soluciones Logísticas (3PL) cross-link card gating. B2B-pillar searches
  // only (Warehouse/Industrial by URL, or the matching space-type filter on
  // the unified /search route) — see `shouldShowLogisticsCard`. Unlike
  // on-demand, this is nationwide with no partner-coverage roster to check.
  const showLogisticsCard = useMemo(
    () => shouldShowLogisticsCard(router.query.pillar, filters.spaceTypes),
    [router.query.pillar, filters.spaceTypes]
  )

  const ref = React.useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!isReady || !isDownMd) return
    if (!ref.current) return
    if (loading) return

    if (!isSliderOpen) {
      ref.current.scrollIntoView({ behavior: 'instant', block: 'start' })
    }
  }, [isDownMd, isReady, loading, isSliderOpen])

  // Build the grid items as an interleaved array. The on-demand card is
  // inserted at `ON_DEMAND_CARD_INDEX`; if there are fewer listings than the
  // index, it appears at the end of whatever listings exist (so 2 results +
  // card → card at position 2, after both listings).
  // Has the renter reached the end of SpotMe's own supply?
  //
  // Defaults to TRUE on purpose: `pageCount` is undefined on surfaces that
  // don't paginate and 0/1 when everything fits on one page. The gate must
  // only ever HOLD the floor BACK inside a genuinely multi-page market — it
  // must never hide it where there are no further pages to exhaust, which
  // includes every zero-result page and most hood pages.
  const isLastResultsPage =
    !pageCount || pageCount <= 1 || (page ?? 1) >= pageCount

  const visibleLocations = locations ?? []

  /** True once the ranked query has settled on an empty result set. Drives the
   *  empty-state branch below AND where the directory floor is mounted — the
   *  two must agree, or the floor renders twice. */
  const isZeroResults = !loading && visibleLocations.length === 0
  /** A zero-SUPPLY city page that arrived with SSR directory rows: the page
   *  renders as a city guide, not as a failed search (2026-10-20). */
  const directoryGuideCount = isZeroResults
    ? (initialDirectory?.length ?? 0)
    : 0
  const isDirectoryGuide = directoryGuideCount > 0
  /** A WEAK-supply mini-bodega page (2026-10-20): the SSR guide follows the
   *  SpotMe listings and replaces the lazy floor. Results exist, so every
   *  SpotMe listing renders first — the guide is a separate block below the
   *  grid and can never interleave with it. */
  const isWeakDirectoryGuide =
    !loading &&
    !isZeroResults &&
    isLastResultsPage &&
    (initialDirectory?.length ?? 0) > 0

  /**
   * The same rule for searches that arrived WITHOUT server-rendered rows —
   * chiefly `/search` (Jules, 2026-10-20: "storage in Cancún, not the city
   * page, the search"). The lazy floor then renders as the guide, and in weak
   * mode it moves up to right after the SpotMe grid. Pages that carried SSR
   * rows keep `DirectoryZeroSupply` above and never reach this.
   */
  const clientGuideMode =
    loading || (initialDirectory?.length ?? 0) > 0
      ? null
      : directoryGuideMode({
          pillar: guidePillarFor(
            router.query.pillar,
            getEffectiveSpaceTypes(filters)
          ),
          searchTotal: totalLocations,
          center:
            gateLat != null && gateLng != null
              ? { lat: gateLat, lng: gateLng }
              : null
        })
  const isClientWeakGuide =
    clientGuideMode === 'weak' && !isZeroResults && isLastResultsPage

  const gridItems: React.ReactNode[] = []
  if (visibleLocations.length > 0) {
    visibleLocations.forEach((location, i) => {
      if (onDemandResolution && i === ON_DEMAND_CARD_INDEX) {
        gridItems.push(
          <OnDemandSearchCard
            key="on-demand-search-card"
            canonicalCity={onDemandResolution.city}
            cityConfig={onDemandResolution.config}
          />
        )
      }
      if (showLogisticsCard && i === LOGISTICS_CARD_INDEX) {
        gridItems.push(
          <LogisticsSearchCard key="logistics-search-card" variant="grid" />
        )
      }
      gridItems.push(
        <LocationSearchCard
          key={location.id}
          location={location}
          toggleLocation={setSelectedLocation}
          priority={priorityImages && i === 0}
          position={i + 1}
        />
      )
    })
    if (
      onDemandResolution &&
      visibleLocations.length > 0 &&
      visibleLocations.length <= ON_DEMAND_CARD_INDEX
    ) {
      gridItems.push(
        <OnDemandSearchCard
          key="on-demand-search-card"
          canonicalCity={onDemandResolution.city}
          cityConfig={onDemandResolution.config}
        />
      )
    }
    if (
      showLogisticsCard &&
      visibleLocations.length > 0 &&
      visibleLocations.length <= LOGISTICS_CARD_INDEX
    ) {
      gridItems.push(
        <LogisticsSearchCard key="logistics-search-card" variant="grid" />
      )
    }
  }

  /* The directory tier floor. Built once here and mounted in exactly ONE of
     two places — hoisted inside the zero-results block, or last on the
     with-results path. Never rendered while the ranked results are still
     loading, so it can't flash as the only content on the page. */
  const directoryFloor = (
    <DirectoryFloorMount
      expectedRows={directoryExpectedRows}
      onHoverChange={onDirectoryHoverChange}
      zeroResults={isZeroResults}
      guideMode={clientGuideMode}
      locationName={locationName ?? locationDisplayName ?? undefined}
      center={
        filters.lat && filters.lng
          ? { lat: filters.lat, lng: filters.lng }
          : undefined
      }
      zoom={filters.zoom}
      spaceTypes={getEffectiveSpaceTypes(filters)}
      placeId={filters.placeId !== NEAR_USER ? filters.placeId : undefined}
    />
  )

  return (
    <>
      <div
        className="mt-3 mb-2 flex w-full flex-col px-4 text-sm md:my-3 md:px-6"
        ref={ref}
      >
        <div className="flex items-center gap-2 text-ink-200 md:justify-start">
          {/* `key` forces remount when the count or noun changes — the
              `animate-fade-in` keyframe (200ms ease-out, defined in
              tailwind.config.js) plays on each remount, giving the pill
              a subtle fade when results update post-refetch. */}
          <span
            key={`${numberOfLocations}-${countNoun}`}
            className="shrink-0 animate-fade-in rounded-full bg-blue-500/10 px-2.5 py-0.5 text-xs font-semibold text-blue-800"
          >
            {/* Single text node, not `{count} {noun}` — that splits into
                expr→space→expr and React emits TWO `<!-- -->` boundary
                comments (`3<!-- --> <!-- -->Estacionamientos`) that
                comment-stripping browsers/webviews remove → React 19 #418.
                Confirmed firing on /pension-estacionamiento/* hood pages
                (Datadog 2026-06-12). Same fix as the gallery "Ver N fotos"
                pill. See CLAUDE.md "Trigger H". */}
            {isDirectoryGuide
              ? t('SEARCH_MANAGER.DIRECTORY_GUIDE_CHIP', {
                  count: directoryGuideCount
                })
              : isZeroResults
                ? t('SEARCH_MANAGER.NO_MATCHES')
                : `${numberOfLocations} ${countNoun}`}
          </span>
          <span className="truncate text-xs text-ink-200">{locationLabel}</span>
        </div>
      </div>

      <div
        className={`mx-4 overflow-hidden rounded-full border border-blue-500/40 bg-blue-500/5 py-1.5 md:mx-6 md:mt-0 md:mb-4 ${header ? 'hidden' : ''}`}
      >
        <p className="text-center text-xs font-medium text-blue-800">
          <GppGoodIcon className="mt-[-1px] mr-1 text-sm" />
          {t('SEARCH_MANAGER.VERIFIED_HOSTS')}
        </p>
      </div>

      {header}

      {header && <BuenFinBanner containerClassName="bg-white-400 md:px-6" />}

      <div className="px-0 py-1.5 md:p-3 md:py-0">
        {loading ? (
          <LoadingCards />
        ) : isZeroResults ? (
          <div className="px-3 pt-4 md:pt-4">
            {/* "Prueba otra zona o ajusta los filtros" is the wrong first line
                on a city guide — there is nothing wrong with the search, the
                answer just isn't SpotMe supply yet. The WhatsApp help it
                carried still follows the guide, in <CaptureLead>. */}
            {!isDirectoryGuide && (
              <EmptySearchSummary
                locationName={locationName}
                nearbySearchData={nearbySearchData}
              />
            )}
            <MarketNotAvailable searchCenter={searchCenter} />
            {/* ZERO-RESULTS ONLY: the floor is HOISTED above the lead form and
                the host pivot (Jules, 2026-08-24). Below them it was, in
                practice, invisible — on mobile there is no map and no markers,
                so a renter met an empty state, a form and a host pitch and
                concluded the zone had nothing, while a directory of real
                facilities sat under ~two screens of scroll.

                This does NOT weaken the anti-dilution rule. That rule is about
                never letting an unclaimed facility outrank MONETIZED supply —
                and on this branch there is none by definition. The moment the
                zone has a single SpotMe listing the floor drops back below
                everything (see the mount at the foot of this component), which
                stays the ordering on every page that has results.

                Consequence to know: the sentinel is now near the top of a short
                page, so a zero-result page DOES ship the directory chunk + its
                i18n namespace on load. The shared `directoryLocations` read was
                already eager (the map owns it), so this costs a chunk, not a
                round-trip. */}
            {/* `-mx-3` cancels this block's own padding: the floor's
                <section> already carries `px-3`, and nesting the two would
                inset its cards 12px deeper than the empty-state and lead-form
                cards they sit between. */}
            {/* 2026-08-28: when the page arrived with SSR directory rows (a
                zero-SUPPLY city, not merely a zero-result filter), render
                them into the HTML instead of the lazy floor — crawlable and
                above the fold. The lazy floor stays for client-side searches. */}
            {initialDirectory && initialDirectory.length > 0 ? (
              <DirectoryZeroSupply
                locations={initialDirectory}
                locationName={locationName}
                mode="zero"
              />
            ) : (
              <div className="-mx-3">{directoryFloor}</div>
            )}
            <div className="mt-8 md:mt-12">
              <CaptureLead
                locationName={locationName}
                zeroResults
                nearbySearchData={nearbySearchData}
              />
              <div className="mt-8 md:mt-12">
                <HostCTA
                  nearbySearchData={nearbySearchData}
                  locationName={locationName}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-2 mb-6 flex flex-col gap-5 px-3 md:mt-6 md:mb-0">
              {gridItems}
            </div>

            {/* Weak supply: the guide sits right after the SpotMe listings and
                AHEAD of the lead form + host pivot (Jules, 2026-10-20 —
                "promote the directory, just keep SpotMe listings on top").
                This deliberately relaxes the floor's below-<CaptureLead> rule
                for these pages only; Monterrey never gets here
                (`directoryGuideMode`). */}
            {isWeakDirectoryGuide && initialDirectory && (
              <div className="mt-8 px-3 md:mt-10">
                <DirectoryZeroSupply
                  locations={initialDirectory}
                  locationName={locationName}
                  mode="weak"
                />
              </div>
            )}

            {/* Same placement for a search with no SSR rows (`/search`). */}
            {isClientWeakGuide && directoryFloor}

            {totalLocations < MIN_LOCS_FORM && (
              // Sparse-results path (1-7 listings). The grid above already
              // includes the inline <OnDemandSearchCard> via gridItems when
              // on-demand coverage is active, so the empty-state recovery
              // hero is intentionally NOT rendered here — only the renter
              // lead form and host pivot follow.
              <div className="mt-8 px-3 md:mt-12">
                <CaptureLead
                  locationName={locationName}
                  nearbySearchData={nearbySearchData}
                />
                <div className="mt-8 md:mt-12">
                  <HostCTA
                    nearbySearchData={nearbySearchData}
                    locationName={locationName}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* WITH-RESULTS path: the floor is LAST, and deliberately below
            <CaptureLead> — an unclaimed facility must never outrank monetized
            supply, and must never get first go at a renter the lead form could
            capture. This subordination is the anti-dilution design and it does
            not change; only the zero-results branch above hoists it, and only
            because that branch has no monetized supply to subordinate to.

            AND only on the FINAL page of results (Jules, 2026-08-23). It used
            to render at the bottom of EVERY page, so in a market with 5 pages
            of real listings a renter met unclaimed facilities at the foot of
            page 1 — competing with page 2 of monetized supply, which is the
            one thing the tier floor exists to prevent. The rule is: every
            SpotMe listing first, the directory only once they are exhausted.
            Unpaginated markets (the majority of hood pages) have a single
            page, so they are unaffected. */}
        {!loading &&
          !isZeroResults &&
          isLastResultsPage &&
          !isWeakDirectoryGuide &&
          !isClientWeakGuide &&
          directoryFloor}
      </div>
    </>
  )
}

export default React.memo(SearchList)
